import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    LeadAdminResponseSchema,
    AdminLeadQueueRowSchema,
    MatchExpertsResponseSchema,
    AdminLeadQueueQueryParams,
    AdminListLeadsQueryParams,
    MatchExpertsRequest,
    AdminUpdateLeadRequest,
} from "../schemas/leads-v2";

export function createAdminLeadsV2Api(http: HttpClient, baseUrl: string) {
    const adminBaseUrl = `${baseUrl}/admin/leads-v2`;

    return {
        /**
         * List all leads with optional filtering by status, expert, or client
         * GET /api/v2/admin/leads-v2
         */
        listLeads: createEndpointWithPagination<
            z.infer<typeof LeadAdminResponseSchema>,
            unknown,
            unknown,
            AdminListLeadsQueryParams
        >(http, "GET", "", {
            base: adminBaseUrl,
            itemSchema: LeadAdminResponseSchema,
        }),

        /**
         * Project-aware queue for the new admin leads + projects manager.
         * GET /api/v2/admin/leads-v2/queue
         */
        listQueue: createEndpointWithPagination<
            z.infer<typeof AdminLeadQueueRowSchema>,
            unknown,
            unknown,
            AdminLeadQueueQueryParams
        >(http, "GET", "/queue", {
            base: adminBaseUrl,
            itemSchema: AdminLeadQueueRowSchema,
        }),

        /**
         * Get a single lead by ID
         * GET /api/v2/admin/leads-v2/:id
         */
        getLead: createEndpoint<
            z.infer<typeof LeadAdminResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "GET", "/:id", {
            base: adminBaseUrl,
            responseSchema: LeadAdminResponseSchema,
            params: ["id"],
        }),

        /**
         * Update a lead
         * PUT /api/v2/admin/leads-v2/:id
         */
        updateLead: createEndpoint<
            z.infer<typeof LeadAdminResponseSchema>,
            AdminUpdateLeadRequest,
            QueryParams,
            { id: number }
        >(http, "PUT", "/:id", {
            base: adminBaseUrl,
            responseSchema: LeadAdminResponseSchema,
            params: ["id"],
        }),

        /**
         * Match one or more experts to a lead
         * POST /api/v2/admin/leads-v2/:id/match
         */
        matchExperts: createEndpoint<
            z.infer<typeof MatchExpertsResponseSchema>,
            MatchExpertsRequest,
            QueryParams,
            { id: number }
        >(http, "POST", "/:id/match", {
            base: adminBaseUrl,
            responseSchema: MatchExpertsResponseSchema,
            params: ["id"],
        }),

        /**
         * Soft delete a lead
         * DELETE /api/v2/admin/leads-v2/:id
         */
        deleteLead: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { id: number }
        >(http, "DELETE", "/:id", {
            base: adminBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["id"],
        }),
    };
}

export type AdminLeadsV2Api = ReturnType<typeof createAdminLeadsV2Api>;
